<template>
    <!-- Show enable billing component if not billable -->
    <kiosk-org-unit-enable-billing
        v-if="orgUnit && !orgUnit.is_billable"
        :teamId="props.teamId"
        :orgUnitId="props.orgUnitId"
    />

    <!-- Show config component if billable -->
    <kiosk-org-unit-details
        v-else-if="orgUnit && orgUnit.is_billable"
        :teamId="props.teamId"
        :orgUnitId="props.orgUnitId"
    >
        <div class="flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-org-unit-billing-config" class="mb-4" />

                <page-header>Billing Config</page-header>

                <page-loading v-if="!seeded"></page-loading>
                <vx-expandable-card v-else class="mb-4">
                    <template v-slot:header><span :id="paymentMethodLabelId">Payment Method & Terms</span></template>
                    <template v-slot:content>
                        <div class="flex flex-col gap-6 mt-3">
                            <div
                                role="radiogroup"
                                :aria-labelledby="paymentMethodLabelId"
                                class="flex flex-col gap-2"
                                data-test="payment-methods"
                            >
                                <label class="flex items-center gap-2 pb-2">
                                    <input
                                        type="radio"
                                        v-model="billingOption"
                                        :name="billingOptionName"
                                        value="automatic"
                                        :disabled="team?.status === 'closed'"
                                        class="w-4 h-4"
                                    />
                                    <span>Automatic card payment (due immediately)</span>
                                </label>
                                <div class="flex items-start gap-2 pb-2">
                                    <input
                                        type="radio"
                                        :id="invoiceOptionId"
                                        v-model="billingOption"
                                        :name="billingOptionName"
                                        value="invoice"
                                        :disabled="team?.status === 'closed'"
                                        class="w-4 h-4 mt-1"
                                    />
                                    <div class="flex flex-col gap-2 flex-1">
                                        <label :for="invoiceOptionId">Invoice with payment terms</label>
                                        <div v-if="billingOption === 'invoice'" class="flex items-center gap-2">
                                            <span :id="paymentTermsLabelId" class="text-sm">Payment terms:</span>
                                            <vx-input
                                                v-model="form.payment_terms_days"
                                                :aria-labelledby="paymentTermsLabelId"
                                                :aria-describedby="
                                                    paymentTermsError
                                                        ? `${paymentTermsHintId} ${paymentTermsErrorId}`
                                                        : paymentTermsHintId
                                                "
                                                type="number"
                                                :min="1"
                                                :max="120"
                                                size="sm"
                                                class="w-20"
                                                :disabled="team?.status === 'closed'"
                                                @blur="validatePaymentTerms"
                                            />
                                            <span class="text-sm">days</span>
                                        </div>
                                        <span
                                            v-if="billingOption === 'invoice'"
                                            :id="paymentTermsHintId"
                                            class="text-xs text-gray-500"
                                        >
                                            e.g., 30 for Net 30, 60 for Net 60 (max 120 days)
                                        </span>
                                        <span
                                            v-if="paymentTermsError && billingOption === 'invoice'"
                                            :id="paymentTermsErrorId"
                                            class="text-xs text-red-600"
                                        >
                                            {{ paymentTermsError }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="team?.status !== 'closed'">
                                <vx-button
                                    data-test="submit-button"
                                    @click="submit"
                                    size="lg"
                                    color="primary"
                                    :loading="submitting"
                                    :disabled="!!paymentTermsError"
                                    class="w-full"
                                >
                                    Update
                                </vx-button>
                            </div>
                        </div>
                    </template>
                </vx-expandable-card>

                <!-- Pricing Card - Only for billable org units -->
                <kiosk-org-unit-pricing-card
                    v-if="team && orgUnit && orgUnit.is_billable"
                    :team="team"
                    :teamId="props.teamId"
                    :orgUnit="orgUnit"
                    :orgUnitId="props.orgUnitId"
                    @updated="handlePricingUpdated"
                />

                <!-- Alert when not billable -->
                <vx-alert v-else-if="orgUnit && !orgUnit.is_billable" color="info" :closeable="false" class="mb-4">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleInfo" />
                    </template>
                    Pricing configuration is only available for billable organizational units.
                </vx-alert>
            </div>
        </div>
    </kiosk-org-unit-details>
</template>

<script setup>
import { ref, useId, watch } from 'vue';
import KioskOrgUnitDetails from '~/components/kiosk/kiosk-org-units/details/KioskOrgUnitDetails.vue';
import KioskOrgUnitEnableBilling from './KioskOrgUnitEnableBilling.vue';
import KioskOrgUnitPricingCard from '~/components/kiosk/kiosk-org-units/cards/KioskOrgUnitPricingCard.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useOrgUnitDetails } from '~/components/kiosk/kiosk-org-units/composables/useOrgUnitDetails';
import { VxButton, VxExpandableCard, VxInput, VxAlert } from '@voxie/frontend-components';
import adminOrgUnitsClient from '~/services/adminOrgUnitsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
});

const { orgUnit, team, billing, getBilling, onMountedOrgUnit, refreshOrgUnit } = useOrgUnitDetails();
const toasted = useToasted();

const form = ref({
    payment_terms_days: '0',
});

const submitting = ref(false);
const paymentTermsError = ref(null);
const billingOption = ref('automatic');
const seeded = ref(false);

const paymentMethodLabelId = useId();
const billingOptionName = useId();
const invoiceOptionId = useId();
const paymentTermsLabelId = useId();
const paymentTermsHintId = useId();
const paymentTermsErrorId = useId();

watch(billingOption, (newValue) => {
    if (newValue === 'automatic') {
        form.value.payment_terms_days = '0';
    } else if (newValue === 'invoice') {
        // Auto-populate with 30 days if currently 0
        if (form.value.payment_terms_days === '0' || form.value.payment_terms_days === 0) {
            form.value.payment_terms_days = '30';
        }
    }
});

const validatePaymentTerms = () => {
    const days = parseInt(form.value.payment_terms_days) || 0;
    if (days > 0) {
        if (days > 120) {
            paymentTermsError.value = 'Payment terms cannot exceed 120 days';
        } else {
            paymentTermsError.value = null;
        }
    } else {
        paymentTermsError.value = null;
    }
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    validatePaymentTerms();
    if (paymentTermsError.value) {
        return;
    }

    submitting.value = true;

    try {
        const data = {
            payment_terms_days: parseInt(form.value.payment_terms_days) || 0,
        };

        await adminOrgUnitsClient.updateBillingOptions(props.teamId, props.orgUnitId, data);

        await getBilling();

        toasted.success('Payment terms have been successfully updated');
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};

const handlePricingUpdated = async () => {
    await refreshOrgUnit(props.teamId, props.orgUnitId);
};

onMountedOrgUnit(async () => {
    await getBilling();

    if (seeded.value || !billing.value?.is_billable) {
        return;
    }

    form.value = {
        payment_terms_days: String(billing.value.payment_terms_days || 0),
    };

    // Set initial billing option based on payment terms
    if (billing.value.payment_terms_days === 0) {
        billingOption.value = 'automatic';
    } else {
        billingOption.value = 'invoice';
    }

    seeded.value = true;
});
</script>
